import { useCallback, useEffect, useRef, useState } from 'react';

import {
  confirmWeighingRequest,
  createWeighingRequest,
  fetchActiveRequestForRegister,
  fetchActiveRequestLoadSummaries,
  rejectWeighingRequest,
} from './kioskApi';
import {
  calculateMinimumReturnQuantityPacks,
  calculateReturnRemainderQuantityPacks,
  canCreateNextRequest,
  mergeRequests,
} from './kioskTransforms';

const IDLE_STATE = { type: 'idle', message: '' };

export function useKioskRequestWorkflow({
  closeRequestLoadModal,
  createRequestDirection,
  createRequestDirectionRef,
  currentRequest,
  openAcceptLoadModal,
  operatorActorName,
  operatorForm,
  operatorSource,
  registerOperationBlocker,
  requestLoads,
  requestRefreshTick,
  scannedProduct,
  scannedQuantityPacks,
  selectedRegisterId,
  selectedRequestLoadId,
  setCurrentRequest,
  setReadings,
  setRequestLoadActionState,
  setRequestLoads,
  setRequestState,
  setRequests,
  visibilityRefreshTick,
}) {
  const [createState, setCreateState] = useState(IDLE_STATE);
  const [confirmState, setConfirmState] = useState(IDLE_STATE);
  const [rejectState, setRejectState] = useState(IDLE_STATE);
  const [quantityMismatch, setQuantityMismatch] = useState(null);
  const confirmInFlightRef = useRef(false);
  const quantityMismatchOpenRef = useRef(false);

  useEffect(() => {
    setCreateState(IDLE_STATE);
    setConfirmState(IDLE_STATE);
    setRejectState(IDLE_STATE);
    if (quantityMismatchOpenRef.current) {
      quantityMismatchOpenRef.current = false;
      confirmInFlightRef.current = false;
      setQuantityMismatch(null);
    }
  }, [requestRefreshTick, selectedRegisterId, visibilityRefreshTick]);

  useEffect(() => {
    if (!quantityMismatchOpenRef.current) {
      return;
    }

    quantityMismatchOpenRef.current = false;
    confirmInFlightRef.current = false;
    setQuantityMismatch(null);
  }, [
    currentRequest?.requestDirection,
    currentRequest?.requestId,
    currentRequest?.selectedScaleReadingId,
    currentRequest?.selectedScannerReadingId,
  ]);

  const createStationRequest = useCallback(async (options = {}) => {
    async function createWithConflictRecovery(attemptOptions) {
      const allowConflictRetry = attemptOptions.allowConflictRetry ?? true;
      const requestDirection = attemptOptions.requestDirection === 'Inbound' ? 'Inbound' : 'Outbound';

      if (!selectedRegisterId || registerOperationBlocker) {
        return null;
      }

      const displayedActiveRequest = currentRequest?.registerId === selectedRegisterId
        && !canCreateNextRequest(currentRequest)
        ? currentRequest
        : null;
      if (displayedActiveRequest) {
        setCreateState({
          type: 'success',
          message: `Loaded existing station request '${displayedActiveRequest.requestId}'.`,
        });
        return displayedActiveRequest;
      }

      setCreateState({
        type: 'pending',
        message: attemptOptions.pendingMessage ?? 'Creating station request...',
      });

      try {
        const created = await createWeighingRequest({
          registerId: selectedRegisterId,
          requestDirection,
          source: operatorSource,
        });
        setCurrentRequest(created);
        setRequests((currentItems) => mergeRequests(currentItems, created));
        setReadings([]);
        setRequestState({ type: 'success', message: '' });
        setCreateState({
          type: 'success',
          message: attemptOptions.successMessage ?? `Station request '${created.requestId}' is ready.`,
        });
        return created;
      } catch (error) {
        const errorMessage = error instanceof Error
          ? error.message
          : 'Unable to create the station request.';

        if (error instanceof Error && (error.status === 409 || /already\s+has\s+active\s+request/i.test(errorMessage))) {
          try {
            const activeRequest = await fetchActiveRequestForRegister(selectedRegisterId);
            if (activeRequest) {
              setCurrentRequest(activeRequest);
              setRequests((currentItems) => mergeRequests(currentItems, activeRequest));
              setReadings([]);
              setRequestState({ type: 'success', message: '' });
              setCreateState({
                type: 'success',
                message: `Loaded existing station request '${activeRequest.requestId}'.`,
              });
              return activeRequest;
            }
          } catch {
            // Continue to the bounded retry when the active-request lookup fails.
          }

          if (allowConflictRetry) {
            return createWithConflictRecovery({
              ...attemptOptions,
              allowConflictRetry: false,
              pendingMessage: 'Recovering station request...',
            });
          }

          setCreateState({
            type: 'error',
            message: 'ไม่พบคำขอที่ใช้งานอยู่ของสถานีนี้ กรุณากดเตรียมคำขอการชั่งอีกครั้ง.',
          });
          return null;
        }

        setCreateState({ type: 'error', message: errorMessage });
        return null;
      }
    }

    return createWithConflictRecovery(options);
  }, [
    currentRequest,
    operatorSource,
    registerOperationBlocker,
    selectedRegisterId,
    setCurrentRequest,
    setReadings,
    setRequestState,
    setRequests,
  ]);

  const confirmCurrentRequestWithLoad = useCallback(async (requestLoadId, options = {}) => {
    if (!currentRequest?.requestId || !requestLoadId || confirmInFlightRef.current) {
      return;
    }

    const requestDirection = createRequestDirectionRef.current === 'Inbound' ? 'Inbound' : 'Outbound';
    const parsedReturnQuantityPacks = Number(operatorForm.returnQuantityPacks);
    const confirmedQuantityPacks = requestDirection === 'Inbound'
      && Number.isInteger(parsedReturnQuantityPacks)
      && parsedReturnQuantityPacks > 0
      ? parsedReturnQuantityPacks
      : null;

    if (requestDirection === 'Inbound') {
      const requestLoad = options.requestLoadOverride
        ?? requestLoads.find((candidate) => candidate.loadId === requestLoadId);
      const minimumQuantityPacks = calculateMinimumReturnQuantityPacks({
        availableQuantityPacks: scannedQuantityPacks,
        loadProducts: requestLoad?.aggregate?.products,
        scannedProduct,
      });
      const remainingQuantityPacks = calculateReturnRemainderQuantityPacks({
        loadProducts: requestLoad?.aggregate?.products,
        scannedProduct,
      });

      if (
        confirmedQuantityPacks != null
        && minimumQuantityPacks != null
        && confirmedQuantityPacks < minimumQuantityPacks
      ) {
        confirmInFlightRef.current = true;
        quantityMismatchOpenRef.current = true;
        setQuantityMismatch({
          type: 'belowMinimum',
          enteredQuantityPacks: confirmedQuantityPacks,
          minimumQuantityPacks,
        });
        closeRequestLoadModal();
        return;
      }

      if (
        !options.allowQuantityAboveRemaining
        && confirmedQuantityPacks != null
        && remainingQuantityPacks != null
        && confirmedQuantityPacks > remainingQuantityPacks
      ) {
        confirmInFlightRef.current = true;
        quantityMismatchOpenRef.current = true;
        setQuantityMismatch({
          type: 'aboveRemaining',
          enteredQuantityPacks: confirmedQuantityPacks,
          remainingQuantityPacks,
          requestLoadId,
        });
        closeRequestLoadModal();
        return;
      }
    }

    confirmInFlightRef.current = true;
    setConfirmState({ type: 'pending', message: 'กำลังยืนยันการชั่ง...' });
    setRejectState(IDLE_STATE);
    setCreateState(IDLE_STATE);
    setRequestLoadActionState(IDLE_STATE);

    try {
      const normalizedSaved = await confirmWeighingRequest(currentRequest.requestId, {
        requestLoadId,
        requestDirection,
        confirmedBy: operatorActorName,
        confirmationNotes: operatorForm.confirmationNotes.trim(),
        quantityPacks: confirmedQuantityPacks,
        source: operatorSource,
      });
      setRequests((currentItems) => mergeRequests(currentItems, normalizedSaved));
      setRequestLoads((currentItems) => currentItems.map((requestLoad) => (
        requestLoad.loadId === requestLoadId
          ? { ...requestLoad, status: requestLoad.status === 'Open' ? 'InProgress' : requestLoad.status }
          : requestLoad
      )));

      const [nextRequest, refreshedRequestLoads] = await Promise.all([
        fetchActiveRequestForRegister(selectedRegisterId),
        fetchActiveRequestLoadSummaries().catch(() => null),
      ]);
      if (refreshedRequestLoads) {
        setRequestLoads(refreshedRequestLoads);
      }
      if (nextRequest) {
        setCurrentRequest(nextRequest);
        setRequests((currentItems) => mergeRequests(currentItems, nextRequest));
        setReadings([]);
      } else {
        setCurrentRequest(normalizedSaved);
      }

      closeRequestLoadModal();
      setConfirmState({
        type: 'success',
        message: `การชั่ง '${normalizedSaved.requestId}' ถูกยืนยันเรียบร้อยแล้ว.`,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ไม่สามารถยืนยันการชั่งได้.';
      if (/already\s+confirmed/i.test(errorMessage)) {
        try {
          const nextRequest = await fetchActiveRequestForRegister(selectedRegisterId);
          if (nextRequest) {
            setCurrentRequest(nextRequest);
            setRequests((currentItems) => mergeRequests(currentItems, nextRequest));
            setReadings([]);
          }
        } catch {
          // Report idempotent success even if the recovery refresh fails.
        }

        closeRequestLoadModal();
        setConfirmState({
          type: 'success',
          message: 'การชั่งนี้ถูกยืนยันไปแล้ว ระบบอัปเดตสถานะล่าสุดให้เรียบร้อย.',
        });
        return;
      }

      setConfirmState({ type: 'error', message: errorMessage });
    } finally {
      confirmInFlightRef.current = false;
    }
  }, [
    closeRequestLoadModal,
    createRequestDirectionRef,
    currentRequest?.requestId,
    operatorActorName,
    operatorForm.confirmationNotes,
    operatorForm.returnQuantityPacks,
    operatorSource,
    requestLoads,
    scannedProduct,
    scannedQuantityPacks,
    selectedRegisterId,
    setCurrentRequest,
    setReadings,
    setRequestLoadActionState,
    setRequestLoads,
    setRequests,
  ]);

  const cancelQuantityMismatchConfirmation = useCallback(() => {
    quantityMismatchOpenRef.current = false;
    confirmInFlightRef.current = false;
    setQuantityMismatch(null);
  }, []);

  const continueQuantityMismatchConfirmation = useCallback(async () => {
    if (quantityMismatch?.type !== 'aboveRemaining' || !quantityMismatch.requestLoadId) {
      return;
    }

    const requestLoadId = quantityMismatch.requestLoadId;
    quantityMismatchOpenRef.current = false;
    confirmInFlightRef.current = false;
    setQuantityMismatch(null);
    await confirmCurrentRequestWithLoad(requestLoadId, {
      allowQuantityAboveRemaining: true,
    });
  }, [confirmCurrentRequestWithLoad, quantityMismatch]);

  const handleConfirmRequest = useCallback(async () => {
    if (!currentRequest?.requestId) {
      return;
    }

    const selectedLoadForRequest = selectedRequestLoadId || currentRequest.requestLoadId;
    if (!selectedLoadForRequest) {
      openAcceptLoadModal();
      return;
    }

    await confirmCurrentRequestWithLoad(selectedLoadForRequest);
  }, [
    confirmCurrentRequestWithLoad,
    currentRequest?.requestId,
    currentRequest?.requestLoadId,
    openAcceptLoadModal,
    selectedRequestLoadId,
  ]);

  const handleCreateRequest = useCallback(async () => {
    await createStationRequest({ requestDirection: createRequestDirection });
  }, [createRequestDirection, createStationRequest]);

  const resetConfirmationState = useCallback(() => {
    setConfirmState(IDLE_STATE);
  }, []);

  const handleRejectRequest = useCallback(async () => {
    if (!currentRequest?.requestId) {
      return;
    }

    const confirmed = window.confirm(`ปฏิเสธการชั่ง '${currentRequest.requestId}'?`);
    if (!confirmed) {
      return;
    }

    setRejectState({ type: 'pending', message: 'กำลังปฏิเสธการชั่ง...' });
    setConfirmState(IDLE_STATE);
    setCreateState(IDLE_STATE);
    setRequestLoadActionState(IDLE_STATE);

    try {
      const normalizedSaved = await rejectWeighingRequest(currentRequest.requestId, {
        rejectedBy: operatorActorName,
        rejectionNotes: operatorForm.confirmationNotes.trim(),
        source: operatorSource,
      });
      setRequests((currentItems) => mergeRequests(currentItems, normalizedSaved));
      const nextRequest = await fetchActiveRequestForRegister(selectedRegisterId);
      if (nextRequest) {
        setCurrentRequest(nextRequest);
        setRequests((currentItems) => mergeRequests(currentItems, nextRequest));
        setReadings([]);
      } else {
        setCurrentRequest(normalizedSaved);
      }
      setRejectState({
        type: 'success',
        message: `การชั่ง '${normalizedSaved.requestId}' ถูกปฏิเสธเรียบร้อยแล้ว.`,
      });
    } catch (error) {
      setRejectState({
        type: 'error',
        message: error instanceof Error ? error.message : 'ไม่สามารถปฏิเสธการชั่งได้.',
      });
    }
  }, [
    currentRequest?.requestId,
    operatorActorName,
    operatorForm.confirmationNotes,
    operatorSource,
    selectedRegisterId,
    setCurrentRequest,
    setReadings,
    setRequestLoadActionState,
    setRequests,
  ]);

  return {
    confirmCurrentRequestWithLoad,
    confirmState,
    createState,
    createStationRequest,
    handleConfirmRequest,
    handleCreateRequest,
    handleRejectRequest,
    cancelQuantityMismatchConfirmation,
    continueQuantityMismatchConfirmation,
    quantityMismatch,
    rejectState,
    resetConfirmationState,
  };
}
